export interface Position {
  x: number; // mm from left
  y: number; // mm from top
}

export interface Size {
  width: number; // mm
  height: number; // mm
}

export interface BaseElement {
  id: string;
  type: 'text' | 'qrcode' | 'barcode' | 'image' | 'shape' | 'icon';
  position: Position;
  size: Size;
  locked?: boolean;
  visible?: boolean;
  rotation?: number; // degrees
  zIndex?: number;
  content?: string; // Optional content for elements that support it
  field?: string; // Optional field binding
}

export interface TextElement extends BaseElement {
  type: 'text';
  content: string;
  fontSize: number; // pt
  fontFamily: string;
  fontWeight: 'normal' | 'bold';
  textAlign: 'left' | 'center' | 'right';
  color?: string;
  field?: string; // Dynamic field name
}

export interface QRCodeElement extends BaseElement {
  type: 'qrcode';
  content: string;
  field?: string;
  foregroundColor?: string;
  backgroundColor?: string;
}

export interface BarcodeElement extends BaseElement {
  type: 'barcode';
  content: string;
  field?: string;
  includeText: boolean;
  barcodeType?: BarcodeType;
  barScale?: number; // Horizontal stretch factor (0.5-8), default 2
  barHeightRatio?: number; // Ratio of bar height to element height (0.1-1.0), default 0.8
  quietZone?: number; // Quiet zone / margin in modules (0-20), default 2
  foregroundColor?: string;
  backgroundColor?: string;
  textSize?: number; // Text size below barcode (6-16), default 8
}

export type BarcodeType = 
  | 'code128' | 'code39' | 'ean13' | 'ean8' | 'upca'
  | 'code93' | 'itf14' | 'datamatrix' | 'pdf417'
  | 'gs1-128' | 'interleaved2of5' | 'codabar' | 'msi'
  | 'pharmacode' | 'telepen';

export const BARCODE_TYPE_OPTIONS = [
  { value: 'code128', label: 'Code 128', description: 'Alphanumeric, most versatile', category: 'General' },
  { value: 'code39', label: 'Code 39', description: 'Uppercase letters + digits', category: 'General' },
  { value: 'code93', label: 'Code 93', description: 'Compact alternative to Code 39', category: 'General' },
  { value: 'codabar', label: 'Codabar', description: 'Libraries, blood banks, shipping', category: 'General' },
  { value: 'ean13', label: 'EAN-13', description: '13-digit product codes (12 data + check)', category: 'Retail' },
  { value: 'ean8', label: 'EAN-8', description: '8-digit compact product codes', category: 'Retail' },
  { value: 'upca', label: 'UPC-A', description: '12-digit US product codes', category: 'Retail' },
  { value: 'itf14', label: 'ITF-14', description: '14-digit shipping containers', category: 'Logistics' },
  { value: 'interleaved2of5', label: 'Interleaved 2 of 5', description: 'Numeric pairs, warehouse use', category: 'Logistics' },
  { value: 'gs1-128', label: 'GS1-128', description: 'Supply chain with application IDs', category: 'Logistics' },
  { value: 'datamatrix', label: 'Data Matrix', description: '2D matrix, very compact', category: '2D' },
  { value: 'pdf417', label: 'PDF417', description: '2D stacked, high capacity (IDs, transport)', category: '2D' },
  { value: 'msi', label: 'MSI Plessey', description: 'Inventory & warehouse shelves', category: 'Specialty' },
  { value: 'pharmacode', label: 'Pharmacode', description: 'Pharmaceutical packaging', category: 'Specialty' },
  { value: 'telepen', label: 'Telepen', description: 'Full ASCII encoding', category: 'Specialty' },
] as const;

// Sample content per barcode type (valid defaults for each format)
export const BARCODE_SAMPLE_CONTENT: Record<string, string> = {
  code128: '123456789',
  code39: 'HELLO123',
  code93: 'HELLO123',
  codabar: 'A123456A',
  ean13: '5901234123457',
  ean8: '96385074',
  upca: '012345678905',
  itf14: '10012345678902',
  interleaved2of5: '1234567890',
  'gs1-128': '(01)12345678901234',
  datamatrix: 'Hello World',
  pdf417: 'Hello World',
  msi: '123456',
  pharmacode: '1234',
  telepen: 'Hello',
};

// Validation rules per barcode type
export const BARCODE_VALIDATION: Record<string, { regex?: RegExp; message: string; minLength?: number; maxLength?: number }> = {
  code128: { message: 'Any ASCII characters' },
  code39: { regex: /^[A-Z0-9\-. $/+%]*$/, message: 'Uppercase A-Z, digits 0-9, - . $ / + % space' },
  code93: { regex: /^[A-Z0-9\-. $/+%]*$/, message: 'Uppercase A-Z, digits 0-9, - . $ / + % space' },
  ean13: { regex: /^\d{12,13}$/, message: 'Exactly 12 or 13 digits', minLength: 12, maxLength: 13 },
  ean8: { regex: /^\d{7,8}$/, message: 'Exactly 7 or 8 digits', minLength: 7, maxLength: 8 },
  upca: { regex: /^\d{11,12}$/, message: 'Exactly 11 or 12 digits', minLength: 11, maxLength: 12 },
  itf14: { regex: /^\d{13,14}$/, message: 'Exactly 13 or 14 digits', minLength: 13, maxLength: 14 },
  interleaved2of5: { regex: /^\d+$/, message: 'Even number of digits', minLength: 2 },
  'gs1-128': { message: 'GS1 Application Identifiers in parentheses, e.g. (01)12345678901234' },
  datamatrix: { message: 'Any text or data' },
  pdf417: { message: 'Any text or data' },
  codabar: { regex: /^[A-Da-d][0-9\-$:/.+]+[A-Da-d]$/, message: 'Start/stop A-D, digits, - $ : / . +' },
  msi: { regex: /^\d+$/, message: 'Digits only' },
  pharmacode: { regex: /^\d+$/, message: 'Number between 3 and 131070', minLength: 1 },
  telepen: { message: 'Any ASCII characters' },
};

export interface ImageElement extends BaseElement {
  type: 'image';
  src: string; // base64 or URL
  objectFit?: 'contain' | 'cover' | 'fill';
  borderRadius?: number;
}

export interface ShapeElement extends BaseElement {
  type: 'shape';
  shapeType: 'rectangle' | 'circle' | 'line' | 'triangle';
  fillColor?: string;
  strokeColor?: string;
  strokeWidth?: number;
  borderRadius?: number;
}

export interface IconElement extends BaseElement {
  type: 'icon';
  iconName: string;
  color?: string;
}

export type LabelElement = TextElement | QRCodeElement | BarcodeElement | ImageElement | ShapeElement | IconElement;

export interface PrefixSource {
  datasetId: string;
  field: string;        // Deprecated, kept for backward compat
  searchField: string;  // Field shown in dropdown to search/select by
  fillField: string;    // Field whose value gets prepended
}

export interface FieldConfig {
  [field: string]: {
    lastChars?: number;       // Take last X characters, 0 = disabled
    stripChars?: string;      // Characters to strip from value
    prefixSources?: PrefixSource[]; // Dataset+field combos for prefix dropdown
  };
}

export interface LabelTemplate {
  id: string;
  name: string;
  width: number; // mm - fixed at 50.8
  height: number; // mm - fixed at 25.4
  elements: LabelElement[];
  createdAt: number;
  updatedAt: number;
  isGlobal?: boolean; // Admin-managed global templates visible to all users
  category?: string; // Template category/folder for organization
  labelRotation?: 0 | 90 | 180 | 270; // Whole-label rotation in degrees
  fieldConfig?: FieldConfig; // Per-field processing settings
}

export const TEMPLATE_CATEGORIES = [
  'Shipping',
  'Inventory',
  'Product',
  'Price Tags',
  'Address',
  'Custom',
] as const;

export type TemplateCategory = typeof TEMPLATE_CATEGORIES[number];

export interface FieldData {
  [key: string]: string;
}

export interface LabelSheetConfig {
  name: string;
  pageWidth: number; // mm (A4 = 210)
  pageHeight: number; // mm (A4 = 297)
  labelWidth: number;
  labelHeight: number;
  columns: number;
  rows: number;
  marginTop: number;
  marginLeft: number;
  horizontalGap: number;
  verticalGap: number;
}

export const createSingleLabelConfig = (width: number, height: number): LabelSheetConfig => ({
  name: 'Single Label',
  pageWidth: width,
  pageHeight: height,
  labelWidth: width,
  labelHeight: height,
  columns: 1,
  rows: 1,
  marginTop: 0,
  marginLeft: 0,
  horizontalGap: 0,
  verticalGap: 0,
});

// Common label sheet presets
export const LABEL_SHEET_PRESETS: LabelSheetConfig[] = [
  createSingleLabelConfig(50.8, 25.4),
  {
    name: 'Avery 5160 (30 labels)',
    pageWidth: 215.9,
    pageHeight: 279.4,
    labelWidth: 66.7,
    labelHeight: 25.4,
    columns: 3,
    rows: 10,
    marginTop: 12.7,
    marginLeft: 4.8,
    horizontalGap: 3.2,
    verticalGap: 0,
  },
  {
    name: 'A4 - 65 Labels',
    pageWidth: 210,
    pageHeight: 297,
    labelWidth: 38.1,
    labelHeight: 21.2,
    columns: 5,
    rows: 13,
    marginTop: 10.7,
    marginLeft: 4.6,
    horizontalGap: 2.5,
    verticalGap: 0,
  },
  {
    name: 'A4 - 24 Labels',
    pageWidth: 210,
    pageHeight: 297,
    labelWidth: 63.5,
    labelHeight: 33.9,
    columns: 3,
    rows: 8,
    marginTop: 13,
    marginLeft: 7.2,
    horizontalGap: 2.5,
    verticalGap: 0,
  },
];

// Constants
export const LABEL_WIDTH_MM = 50.8;
export const LABEL_HEIGHT_MM = 25.4;
export const SCREEN_DPI = 96;
export const PRINT_DPI = 300;

// Conversion helpers
export const mmToPx = (mm: number, dpi: number = SCREEN_DPI): number => {
  return (mm / 25.4) * dpi;
};

export const pxToMm = (px: number, dpi: number = SCREEN_DPI): number => {
  return (px / dpi) * 25.4;
};

export const ptToMm = (pt: number): number => {
  return pt * 0.352778;
};

export const mmToPt = (mm: number): number => {
  return mm / 0.352778;
};

// History types
export interface HistoryState {
  template: LabelTemplate;
  timestamp: number;
}

// Icon library
export const ICON_LIBRARY = [
  'alert-triangle', 'check-circle', 'x-circle', 'info', 'arrow-up', 'arrow-down',
  'arrow-left', 'arrow-right', 'star', 'heart', 'package', 'truck', 'box',
  'shipping', 'barcode', 'qr-code', 'tag', 'hash', 'at-sign', 'phone', 'mail',
  'globe', 'map-pin', 'calendar', 'clock', 'user', 'users', 'settings', 'tool',
  'zap', 'flame', 'droplet', 'thermometer', 'sun', 'moon', 'cloud', 'umbrella',
  'snowflake', 'wind', 'battery', 'wifi', 'bluetooth', 'lock', 'unlock', 'key',
  'shield', 'eye', 'eye-off', 'camera', 'image', 'file', 'folder', 'trash',
  'edit', 'copy', 'scissors', 'download', 'upload', 'printer', 'save', 'refresh',
] as const;

export type IconName = typeof ICON_LIBRARY[number];
